// /site/* 登录与用户中心 API 封装（配合 siteClient 自动签名）

import siteHttp from './siteClient'
import type {
  AuthResponse,
  LoginConfig,
  UserInfo,
  EntitlementInfo,
  OAuthProvider,
  HandoffRedeemResult
} from '@/types/auth'

// ==================== 第三方登录（OAuth） ====================

export { type OAuthProvider }

/** 获取第三方登录重定向地址（/site/oauth/redirect/{key}，带签名）；invite=本地邀请码（经签名 state 传递，注册时归因） */
export async function apiOauthRedirect(
  key: string,
  params: { ext_id?: string; ext_nonce?: string; device_id?: string; invite?: string; web_origin: string }
): Promise<{ url: string }> {
  const res = await siteHttp.get<{ code: number; data: { url: string } }>(
    `/site/oauth/redirect/${encodeURIComponent(key)}`,
    { params }
  )
  return res.data.data
}

/** 纯网页回调兑换登录态（/site/auth/handoff/redeem，无签名、无登录态） */
export async function apiHandoffRedeem(code: string): Promise<HandoffRedeemResult> {
  const res = await siteHttp.post<{ code: number; data: HandoffRedeemResult }>(
    '/site/auth/handoff/redeem',
    { code }
  )
  return res.data.data
}

// ==================== 登录/注册（无需登录） ====================

export async function fetchLoginConfig(): Promise<LoginConfig> {
  const res = await siteHttp.get<{ code: number; data: LoginConfig }>('/site/login/config')
  return res.data.data
}

/** 邮箱密码登录（ADR-0041 去用户名；password 为 AES 加密后的密文） */
export async function apiLogin(email: string, encryptedPassword: string): Promise<AuthResponse> {
  const res = await siteHttp.post<{ code: number; data: AuthResponse }>('/site/login', {
    email,
    password: encryptedPassword
  })
  return res.data.data
}

export async function apiSendRegisterCode(email: string, captchaTicket = ''): Promise<void> {
  await siteHttp.post('/site/login/register-code', { email, captcha_ticket: captchaTicket })
}

export async function apiRegister(params: {
  password: string
  email: string
  code: string
  name?: string
  invite_code?: string
}): Promise<AuthResponse> {
  const res = await siteHttp.post<{ code: number; data: AuthResponse }>('/site/login/register', params)
  return res.data.data
}

export async function apiSendResetCode(email: string, captchaTicket = ''): Promise<void> {
  await siteHttp.post('/site/login/reset-code', { email, captcha_ticket: captchaTicket })
}

export async function apiResetPassword(email: string, encryptedPassword: string, code: string): Promise<void> {
  await siteHttp.post('/site/login/reset-password', { email, password: encryptedPassword, code })
}

/** 发送换绑邮箱验证码（登录态；ADR-0041 修复 change_email 死缺口） */
export async function apiSendChangeEmailCode(email: string, captchaTicket = ''): Promise<void> {
  await siteHttp.post('/site/user/email-code', { email, captcha_ticket: captchaTicket })
}

// ==================== 滑块安全验证 ====================

export interface SliderChallenge {
  challenge_id: string
  bg: string
  slider: string
  width: number
  height: number
  gap_size: number
  /** 缺口垂直位置（后端随机，前端据此放置滑块） */
  gap_y: number
}

/** 获取滑块挑战(背景图 + 滑块图 + challenge_id,缺口坐标仅存服务端) */
export async function apiGetSliderChallenge(): Promise<SliderChallenge> {
  const res = await siteHttp.get<{ code: number; data: SliderChallenge }>('/site/captcha/slider')
  return res.data.data
}

/** 校验滑块拖动结果,通过后返回一次性 ticket */
export async function apiVerifySlider(
  challengeId: string,
  x: number,
  duration: number,
  track: { x: number; y: number }[]
): Promise<{ ticket: string }> {
  const res = await siteHttp.post<{ code: number; data: { ticket: string } }>('/site/captcha/slider/verify', {
    challenge_id: challengeId,
    x,
    duration,
    track
  })
  return res.data.data
}

// ==================== 用户中心（需登录） ====================

export async function apiLogout(): Promise<void> {
  await siteHttp.post('/site/logout')
}

/** 登录交接：官网登录成功后登记待领取登录，换一次性 code（token 不进 URL，由扩展 SW 兑换） */
export async function apiIssueHandoff(extId: string, nonce: string): Promise<{ code: string }> {
  const res = await siteHttp.post<{ code: number; data: { code: string } }>('/site/auth/handoff', {
    ext_id: extId,
    nonce
  })
  return res.data.data
}

export async function apiUserInfo(): Promise<UserInfo> {
  const res = await siteHttp.get<{ code: number; data: UserInfo }>('/site/user/info')
  return res.data.data
}

/**
 * 用户状态快照（2026-08-08 合并读：身份 + 设置 + 权益 + state_version，ETag 条件请求）
 * 替代 /site/user/info + /site/user/subscription + /site/user/settings 的多次请求。
 */
export interface UserState {
  user: UserInfo
  settings: Record<string, unknown>
  entitlement: EntitlementInfo
  state_version: string
}

// 同帧共享 Promise：多个调用方（useAuthSync / pushClient / 路由组件）并发时复用同一次请求，
// 避免刷新登录态时重复打 /site/user/state（实测登录态首页刷新会并发 3 次）。
let userStateInflight: Promise<UserState> | null = null

export async function apiUserState(): Promise<UserState> {
  if (userStateInflight) return userStateInflight
  userStateInflight = (async () => {
    try {
      const res = await siteHttp.get<{ code: number; data: UserState }>('/site/user/state')
      return res.data.data
    } finally {
      userStateInflight = null
    }
  })()
  return userStateInflight
}

export async function apiUpdateProfile(name: string): Promise<{ name: string }> {
  const res = await siteHttp.put<{ code: number; data: { name: string } }>('/site/user/profile', { name })
  return res.data.data
}

/** 修改邮箱（新邮箱 + 验证码） */
export async function apiUpdateEmail(email: string, code: string): Promise<{ email: string }> {
  const res = await siteHttp.put<{ code: number; data: { email: string } }>('/site/user/email', { email, code })
  return res.data.data
}

/** 修改/设置密码（均为 AES 密文；oldEncrypted 传 null = 无密码账号首次设置，ADR-0031） */
export async function apiChangePassword(oldEncrypted: string | null, newEncrypted: string): Promise<void> {
  await siteHttp.put('/site/user/password', {
    old_password: oldEncrypted || undefined,
    new_password: newEncrypted
  })
}

/** 注销账户（password 为 AES 密文；无密码 OAuth 账号传 null 免密注销） */
export async function apiDeleteAccount(encryptedPassword: string | null): Promise<void> {
  await siteHttp.post('/site/user/delete', { password: encryptedPassword || undefined })
}

// ==================== 第三方绑定（个人中心，ADR-0041） ====================

export interface OauthBinding {
  provider_type: string
  provider_user_id: string
  create_time: string | null
}

/** 已绑定第三方列表 */
export async function apiOauthList(): Promise<OauthBinding[]> {
  const res = await siteHttp.get<{ code: number; data: { bindings: OauthBinding[] } }>('/site/user/oauth')
  return res.data.data.bindings
}

/** 发起绑定：返回 provider 授权 URL（登录态签发绑定票据 mode=bind） */
export async function apiOauthBind(provider: string): Promise<{ url: string }> {
  const res = await siteHttp.post<{ code: number; data: { url: string } }>(
    `/site/oauth/bind/${encodeURIComponent(provider)}`,
    { web_origin: window.location.origin }
  )
  return res.data.data
}

/** 解绑第三方（保底：解绑后仍须剩 ≥1 种登录标识） */
export async function apiOauthUnbind(provider: string): Promise<void> {
  await siteHttp.delete(`/site/user/oauth/${encodeURIComponent(provider)}`)
}
